import { useEffect, useState, useCallback } from 'react';
import { Activity, AlertTriangle, RefreshCw, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

const formatRelative = (iso) => {
  if (!iso) return 'never run';
  try {
    const then = new Date(iso);
    const diffMs = Date.now() - then.getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
  } catch { return 'unknown'; }
};

const Dot = ({ count, label, testId }) => {
  const ok = count === 0;
  return (
    <div className="flex items-center gap-1.5" data-testid={testId}>
      <span
        className={`w-2.5 h-2.5 rounded-full ${ok ? 'bg-green-500' : 'bg-red-500 animate-pulse'}`}
        title={ok ? 'Healthy' : `${count} flagged`}
      />
      <span className="text-xs text-text-mid whitespace-nowrap">
        {label}
        <span className={`ml-1 font-semibold ${ok ? 'text-green-700' : 'text-red-700'}`}>
          {count}
        </span>
      </span>
    </div>
  );
};

/**
 * BillingHealthWidget — at-a-glance Stripe audit health on the Admin Overview tab.
 * Shows last audit run time + 3 status dots (Orphans · Free-charged · Paid-without-sub).
 * Includes a "Run Now" button that bypasses the 24h throttle.
 */
export default function BillingHealthWidget({ getAuthHeader }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/stripe/admin/audit-health', { headers: getAuthHeader() });
      setData(res.data);
    } catch (e) {
      // Non-fatal — keep widget compact and quiet on the dashboard
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      await api.post('/stripe/admin/run-audit-now', {}, { headers: getAuthHeader() });
      toast.success('Billing audit triggered — refreshing snapshot…');
      // Audit runs synchronously — refresh shortly after
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to trigger audit');
    } finally {
      setRunning(false);
    }
  };

  const totalIssues = data ? (data.orphans + data.free_tier_charged + data.paid_without_sub) : 0;
  const allHealthy = data && totalIssues === 0;

  return (
    <div
      className="bg-white rounded-2xl shadow-card p-5 border border-border-subtle"
      data-testid="billing-health-widget"
    >
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="flex items-center gap-2">
          {allHealthy
            ? <ShieldCheck size={18} className="text-green-600" />
            : data
              ? <AlertTriangle size={18} className="text-red-500" />
              : <Activity size={18} className="text-text-mid" />}
          <h3 className="font-semibold text-navy text-sm">Billing Health</h3>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleRunNow}
          disabled={running}
          className="text-xs h-7 px-2 text-teal hover:text-teal-deep"
          data-testid="billing-health-run-now"
        >
          {running ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          <span className="ml-1">Run Now</span>
        </Button>
      </div>

      {loading ? (
        <p className="text-xs text-text-mid">Loading…</p>
      ) : (
        <>
          <p className="text-xs text-text-mid mb-3" data-testid="billing-health-last-run">
            Last audit: <span className="font-medium text-navy">{formatRelative(data?.last_run_at)}</span>
            {data?.subs_scanned ? <> · <span className="font-mono">{data.subs_scanned}</span> subs scanned</> : null}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            <Dot count={data?.orphans ?? 0} label="Orphans" testId="billing-health-dot-orphans" />
            <Dot count={data?.free_tier_charged ?? 0} label="Free-charged" testId="billing-health-dot-free-charged" />
            <Dot count={data?.paid_without_sub ?? 0} label="Paid w/o sub" testId="billing-health-dot-paid-without" />
          </div>
        </>
      )}
    </div>
  );
}
